import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getInformation, createInformation, updateInformation, deleteInformation, reset } from '../../features/information/informationSlice';
import { useNavigate } from 'react-router-dom';
import Modal from '../../components/Modal';
import { useToast } from '../../contexts/ToastContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const InformationManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const { information, isSuccess, isError, message } = useSelector((state) => state.information);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredInformation = information.filter(info => 
    info.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    info.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
    dispatch(getInformation());
  }, [dispatch, user, navigate]);

  useEffect(() => {
    if (isSuccess) {
      addToast({
        type: 'success',
        message: 'Informasi berhasil disimpan!'
      });
      resetForm();
      dispatch(reset());
    }
    if (isError) {
      addToast({
        type: 'error',
        message: message || 'Gagal menyimpan informasi!'
      });
      dispatch(reset());
    }
  }, [isSuccess, isError, message, dispatch, addToast]);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: '',
    });
    setEditingId(null);
    setIsModalOpen(false);
  };

  const onChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };
  
  const onContentChange = (value) => {
    setFormData(prev => ({ ...prev, content: value }));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      dispatch(updateInformation({ id: editingId, infoData: formData }));
    } else {
      dispatch(createInformation(formData));
    }
  };

  const onEdit = (info) => {
    setEditingId(info._id);
    setFormData({
      title: info.title,
      content: info.content,
      type: info.type,
    });
    setIsModalOpen(true);
  };

  const onDelete = (id) => {
    if (window.confirm('Yakin ingin menghapus informasi ini?')) {
      dispatch(deleteInformation(id));
    }
  };

  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-800">Kelola Informasi</h1>
          <button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Tambah Informasi
          </button>
        </div>
        <div className="w-full md:w-1/3">
          <input
            type="text"
            placeholder="Cari informasi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
          />
        </div>
      </div>
      
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Judul</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipe</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredInformation.map((info) => (
              <tr key={info._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-900 font-medium">{info.title}</td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <span className="px-3 py-1 bg-dark-blue-50 text-dark-blue-700 rounded-full text-xs font-medium">
                    {info.type}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm flex gap-3">
                  <button 
                    onClick={() => onEdit(info)} 
                    className="text-dark-blue-600 hover:text-dark-blue-800 font-medium"
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => onDelete(info._id)} 
                    className="text-red-600 hover:text-red-800 font-medium"
                  >
                    Hapus
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={resetForm}
        title={editingId ? 'Edit Informasi' : 'Tambah Informasi'}
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Judul</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={onChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipe</label>
            <input
              type="text"
              name="type"
              value={formData.type}
              onChange={onChange}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-dark-blue-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Konten</label>
            <ReactQuill
              theme="snow"
              value={formData.content}
              onChange={onContentChange}
              className="bg-gray-50 rounded-xl"
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link', 'blockquote'],
                  ['clean']
                ]
              }}
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-dark-blue-700 text-white rounded-xl font-medium hover:bg-dark-blue-800 transition-colors"
            >
              {editingId ? 'Update' : 'Simpan'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default InformationManagement;
